// Real backend adapter — wires every dealsAPI signature to /api/v1/deals/*
// using the shared axios instance (JWT interceptor inherited). Paths follow
// the backend OpenSpec defined in the deal-management plan.

import { axiosInstance } from './calyxSaaSDashboardAPI'

import type {
  CancelDealReqBody,
  CreateDealReqBody,
  CreateManualInvoiceReqBody,
  CreateShipmentReqBody,
  DealEntity,
  InvoiceEntity,
  ListDealsQuery,
  ListPaginatedDealsResult,
  ProductGroupEntity,
  SendInvoiceReqBody,
  ShipmentEntity,
  UpdateDealReqBody,
  UpdateInvoiceReqBody,
  UpdateShipmentReqBody,
  VoidInvoiceReqBody,
} from './dealsAPI'

const dealsBase = '/api/v1/deals'
const catalogBase = '/api/v1/deal-catalog'

// ─── Deals ─────────────────────────────────────────────────────────────

export const listDeals = async (
  query: ListDealsQuery,
): Promise<ListPaginatedDealsResult> => {
  const { data } = await axiosInstance.get<ListPaginatedDealsResult>(dealsBase, { params: query })
  return data
}

export const getDeal = async (dealId: string): Promise<DealEntity> => {
  const { data } = await axiosInstance.get<DealEntity>(`${dealsBase}/${dealId}`)
  return data
}

export const createDeal = async (body: CreateDealReqBody): Promise<DealEntity> => {
  const { data } = await axiosInstance.post<DealEntity>(dealsBase, body)
  return data
}

export const updateDeal = async (
  dealId: string,
  body: UpdateDealReqBody,
): Promise<DealEntity> => {
  const { data } = await axiosInstance.patch<DealEntity>(`${dealsBase}/${dealId}`, body)
  return data
}

export const archiveDeal = async (dealId: string): Promise<DealEntity> => {
  const { data } = await axiosInstance.post<DealEntity>(`${dealsBase}/${dealId}/archive`)
  return data
}

export const unarchiveDeal = async (dealId: string): Promise<DealEntity> => {
  const { data } = await axiosInstance.post<DealEntity>(`${dealsBase}/${dealId}/unarchive`)
  return data
}

export const cancelDeal = async (
  dealId: string,
  body: CancelDealReqBody,
): Promise<DealEntity> => {
  const { data } = await axiosInstance.post<DealEntity>(`${dealsBase}/${dealId}/cancel`, body)
  return data
}

export const uncancelDeal = async (dealId: string): Promise<DealEntity> => {
  const { data } = await axiosInstance.post<DealEntity>(`${dealsBase}/${dealId}/uncancel`)
  return data
}

// ─── Invoices ──────────────────────────────────────────────────────────

export const listInvoices = async (dealId: string): Promise<InvoiceEntity[]> => {
  const { data } = await axiosInstance.get<{ invoices: InvoiceEntity[] } | InvoiceEntity[]>(
    `${dealsBase}/${dealId}/invoices`,
  )
  return Array.isArray(data) ? data : data?.invoices ?? []
}

export const getInvoice = async (
  dealId: string,
  invoiceId: string,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.get<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}`,
  )
  return data
}

export const createManualInvoice = async (
  dealId: string,
  body: CreateManualInvoiceReqBody,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.post<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices`,
    body,
  )
  return data
}

export const generateNextInvoice = async (dealId: string): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.post<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/generate-next`,
  )
  return data
}

export const updateInvoice = async (
  dealId: string,
  invoiceId: string,
  body: UpdateInvoiceReqBody,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.patch<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}`,
    body,
  )
  return data
}

export const voidInvoice = async (
  dealId: string,
  invoiceId: string,
  body?: VoidInvoiceReqBody,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.post<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}/void`,
    body ?? {},
  )
  return data
}

export const restoreInvoice = async (
  dealId: string,
  invoiceId: string,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.post<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}/restore`,
  )
  return data
}

export const sendInvoice = async (
  dealId: string,
  invoiceId: string,
  body: SendInvoiceReqBody,
  idempotencyKey: string,
): Promise<InvoiceEntity> => {
  const { data } = await axiosInstance.post<InvoiceEntity>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}/send`,
    body,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

export const getInvoicePdfBlob = async (
  dealId: string,
  invoiceId: string,
): Promise<Blob> => {
  const { data } = await axiosInstance.get<Blob>(
    `${dealsBase}/${dealId}/invoices/${encodeURIComponent(invoiceId)}/pdf`,
    { responseType: 'blob' },
  )
  return data
}

// ─── Shipments ─────────────────────────────────────────────────────────

export const listShipments = async (dealId: string): Promise<ShipmentEntity[]> => {
  const { data } = await axiosInstance.get<{ shipments: ShipmentEntity[] } | ShipmentEntity[]>(
    `${dealsBase}/${dealId}/shipments`,
  )
  if (Array.isArray(data)) return data
  return data?.shipments ?? []
}

export const createShipment = async (
  dealId: string,
  body: CreateShipmentReqBody,
): Promise<ShipmentEntity> => {
  const { data } = await axiosInstance.post<ShipmentEntity>(
    `${dealsBase}/${dealId}/shipments`,
    body,
  )
  return data
}

export const updateShipment = async (
  dealId: string,
  shipmentId: string,
  body: UpdateShipmentReqBody,
): Promise<ShipmentEntity> => {
  const { data } = await axiosInstance.patch<ShipmentEntity>(
    `${dealsBase}/${dealId}/shipments/${shipmentId}`,
    body,
  )
  return data
}

export const deleteShipment = async (
  dealId: string,
  shipmentId: string,
): Promise<void> => {
  await axiosInstance.delete(`${dealsBase}/${dealId}/shipments/${shipmentId}`)
}

// ─── Catalog ───────────────────────────────────────────────────────────

export const listProducts = async (): Promise<ProductGroupEntity[]> => {
  const { data } = await axiosInstance.get<ProductGroupEntity[]>(`${catalogBase}/products`)
  return data
}
